/**
 * The cookie contract between this app and `@sportsmet/auth-sdk`.
 *
 * The SDK reads `sportsmet-access-token` on every call and rewrites BOTH
 * cookies itself when it does its reactive 401 refresh — see
 * `refreshAccessToken.ts` under each zone's `sdk-helpers` directory in
 * `packages/auth-sdk`. Those names are therefore a shared contract, not this
 * app's private storage: changing one here without changing it there silently
 * signs everybody out.
 *
 * The SDK's helper is not reachable through the package's `exports` map (only
 * the four zone barrels are), so the names and the write options are restated
 * here. `sessionCookies.test.ts` pins them to the SDK's literals so a drift
 * fails a test instead of production.
 */
import Cookies from "js-cookie";

export const ACCESS_TOKEN_COOKIE_NAME = "sportsmet-access-token";
export const REFRESH_TOKEN_COOKIE_NAME = "sportsmet-refresh-token";

/**
 * How long a written session cookie survives, in seconds.
 *
 * The same thirty days auth-api gives the refresh token
 * (`REFRESH_TOKEN_TTL_SECONDS` in `services/auth-api/src/lib/authConfig.ts`),
 * expressed in the same units so the two are comparable without arithmetic.
 * The server side is the authority; this is a restatement, pinned to that
 * number by `getAuthState.test.ts` the same way the cookie names are, because
 * an app cannot import a service.
 *
 * The two lifetimes have to match. A cookie shorter than the token signs a fan
 * out of a session the server still considers live; a cookie longer than the
 * token leaves a dead token in the browser and turns every page load into a
 * refused refresh.
 */
export const SESSION_COOKIE_TTL_SECONDS = 60 * 60 * 24 * 30;

/**
 * `secure` is derived from the page, not hard-coded: local development runs on
 * plain http at localhost:3001, and a `secure` cookie there is simply dropped —
 * which looks exactly like a broken login.
 *
 * `expires` is computed per call, never hoisted to a module constant. That is
 * what makes the cookie slide: auth-api re-stamps the session's expiry from the
 * moment of every rotation, and every rotation comes back through
 * `writeSessionTokens`, so each write pushes the browser's copy out to match.
 * Hoisting it would freeze the window at whenever this module was first
 * evaluated, which on a long-lived tab is not now.
 */
function cookieOptions(): {
  sameSite: "strict";
  secure: boolean;
  expires: Date;
} {
  const protocol = (globalThis as { location?: { protocol?: string } }).location
    ?.protocol;
  return {
    sameSite: "strict",
    secure: protocol === "https:",
    expires: new Date(Date.now() + SESSION_COOKIE_TTL_SECONDS * 1000),
  };
}

export function readAccessToken(): string | undefined {
  return Cookies.get(ACCESS_TOKEN_COOKIE_NAME);
}

export function readRefreshToken(): string | undefined {
  return Cookies.get(REFRESH_TOKEN_COOKIE_NAME);
}

export function writeSessionTokens({
  accessToken,
  refreshToken,
}: {
  accessToken: string;
  refreshToken?: string | undefined;
}): void {
  const options = cookieOptions();
  Cookies.set(ACCESS_TOKEN_COOKIE_NAME, accessToken, options);
  // A refresh that returns no rotated refresh token keeps the existing one,
  // expiry included. Writing `undefined` would clear it and strand the tab one
  // access-token TTL away from a forced sign-in, and re-writing it with a fresh
  // `expires` would slide the browser past a server window that did not move:
  // auth-api slides `expiresAt` only on a rotation it actually won.
  if (refreshToken !== undefined && refreshToken !== "") {
    Cookies.set(REFRESH_TOKEN_COOKIE_NAME, refreshToken, options);
  }
}

export function clearSessionTokens(): void {
  Cookies.remove(ACCESS_TOKEN_COOKIE_NAME);
  Cookies.remove(REFRESH_TOKEN_COOKIE_NAME);
}
